import { useMemo, useState } from "react";
import { Box, Button, CircularProgress, LinearProgress, Menu, MenuItem, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { FAINT, LINE_STRONG, MONO, MUTED, TEAL, TEXT, btnPrimary, btnSmall, floatSx, menuItemSx } from "./editorTheme";
import { fmtArea, labelOfTerm, PAINT_TERM, termsOfJob } from "./editorModel";
import { useEditorData, workerStarting } from "./useEditorData";
import { useEditorActions } from "./useEditorActions";
import { QueuedTerm, useVegEditor } from "./VegEditorContext";
import { TermPreset } from "../../types/dataReview";

const PRESET_WORDS: Record<string, string[]> = {
  lawn: ["lawn", "grass", "turf", "oval"], trees: ["tree", "canopy", "forest", "eucalypt"], shrubs: ["shrub", "hedge", "bush"],
  pool: ["pool", "dam", "pond", "lake", "water"], bare: ["bare", "sand", "soil", "dirt", "gravel"], paved: ["paved", "driveway", "road", "concrete", "car park"],
  sheds: ["shed", "outbuilding", "garage", "carport"],
};

/** The bar across the top of a refinement: what to find, how the image is
 *  coming along (or how much of a clean slate is mapped), the targets
 *  asked for so far with how far each is reviewed. */
export default function VegEditorCommandBar() {
  const d = useEditorData();
  const a = useEditorActions();
  const { activeTerm, queued, mode } = useVegEditor();
  const [draft, setDraft] = useState("");
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const presets = d.catalogue?.presets ?? [];
  // Typed words are searched exactly as typed. A preset's words only
  // suggest what the findings become ("forest" defaults to Tree), matched
  // as whole words (plurals too), so "street" does not suggest Tree. The
  // suggestion is just the starting "Becomes"; it can be changed after.
  const suggestFor = (text: string): TermPreset | undefined => {
    const lo = text.trim().toLowerCase();
    const hit = (w: string) => new RegExp(`\\b${w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(s|es)?\\b`).test(lo);
    return presets.find((p) => (PRESET_WORDS[p.key] ?? []).some(hit));
  };
  // A preset chip (or the More menu) runs that preset; anything typed is
  // its own search.
  const add = (text: string, preset?: TermPreset) => {
    const t = text.trim();
    if (!t) return;
    const suggested = preset ? undefined : suggestFor(t);
    const term: QueuedTerm = preset
      ? { key: preset.key, label: preset.label, target: preset.target, asset_type: preset.asset_type ?? "" }
      : { phrase: t, label: t.charAt(0).toUpperCase() + t.slice(1), target: suggested?.target ?? "Shrub", asset_type: suggested?.asset_type ?? "" };
    a.lookFor(term);
    setDraft("");
    if (mode === "paint") a.toReview();
  };
  // Targets in the order they were asked for, then anything else the run
  // holds (paint, older passes), then what is waiting its turn.
  const terms = useMemo(() => {
    const seen = new Set<string>();
    const out: { key: string; label: string; state: "done" | "running" | "queued"; count: number; shown: number; decided: number; target: string }[] = [];
    const push = (key: string, state: "done" | "running" | "queued", label?: string, target?: string) => {
      if (seen.has(key)) return;
      const g = d.groups[key];
      // A target with no findings loaded yet is left for the queue to show.
      if (state === "done" && !g) return;
      seen.add(key);
      out.push({
        key, label: label ?? g?.label ?? labelOfTerm(key), state, count: g?.total ?? 0, shown: g?.shown ?? 0,
        decided: g ? g.accepted + g.rejected : 0, target: target ?? g?.target ?? "",
      });
    };
    for (const k of termsOfJob(d.job)) push(k, "done");
    for (const k of Object.keys(d.groups)) push(k, "done");
    // The pass under way is the item sent to the run; the rest wait.
    const stage = (d.job?.stage ?? "").toLowerCase();
    for (const q of queued) {
      const key = q.key ?? `custom:${(q.phrase ?? "").trim().toLowerCase()}`;
      push(key, q.sent ? "running" : "queued", q.label, q.target);
    }
    // A pass started elsewhere (another tab, before a reload) still shows.
    if (d.runningPass && !out.some((t) => t.state === "running")) {
      const m = stage.match(/finding (.+?)(?: \(|$)/);
      if (m) push(`running:${m[1]}`, "running", m[1].charAt(0).toUpperCase() + m[1].slice(1));
    }
    return out;
  }, [d.groups, d.job, d.runningPass, queued]);
  // Presets not asked for yet: a "Try" row until the first target, then a
  // "More" chip at the end of the targets so the bar stays two lines.
  const unused = presets.filter((p) => !terms.some((t) => t.key === p.key));
  const chips = unused.slice(0, 6);
  // What the running pill says: where the run is with it.
  const stageNow = (d.job?.stage ?? "").toLowerCase();
  const runningNote = !d.runningPass ? "starting" : workerStarting(d.job) ? "starting up" : stageNow.includes("finding") ? `${d.job?.progress ?? 0}%` : stageNow.includes("reading") ? "reading photo" : "starting";
  const covered = d.jobSummary ? Math.max(0, d.jobSummary.area_m2 - d.jobSummary.buildings_m2 - d.jobSummary.unmapped_m2) : 0;
  const ground = d.jobSummary ? Math.max(1, d.jobSummary.area_m2 - d.jobSummary.buildings_m2) : 1;
  const covPct = Math.round((covered / ground) * 100);
  return (
    <Box sx={{ ...floatSx, position: "fixed", top: "calc(var(--topbar-h) + var(--strategies-h) + 12px)", left: "50%", transform: "translateX(-50%)", width: 760, maxWidth: "calc(100% - 420px)", p: 1, zIndex: 1100, display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box component="label" sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, height: 42, pl: 1.5, pr: 0.6, borderRadius: 2.25, border: `1px solid ${LINE_STRONG}`, bgcolor: "rgba(0,0,0,0.22)", "&:focus-within": { borderColor: TEAL, boxShadow: "0 0 0 3px rgba(0,212,173,0.16)" } }}>
          <SearchIcon sx={{ color: MUTED, fontSize: 18 }} />
          <input value={draft} onChange={(ev) => setDraft(ev.target.value)} onKeyDown={(ev) => { if (ev.key === "Enter") add(draft); }}
            placeholder="Find on the map — lawn, trees, pools, sheds…" autoComplete="off" data-editor-input="1"
            style={{ flex: 1, minWidth: 0, height: 40, border: 0, background: "transparent", color: TEXT, font: "inherit", fontSize: 14, outline: "none" }} />
          <Button sx={{ ...btnPrimary, ...btnSmall, height: 32 }} onClick={() => add(draft)} disabled={!draft.trim()}>Find</Button>
        </Box>
        {d.preparing && d.job && (
          <Box sx={{ width: 160, flex: "none" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}><CircularProgress size={12} sx={{ color: "#6aa8ff" }} /><span>{workerStarting(d.job) ? "Starting up" : "Preparing image"}</span></Box>
              {!workerStarting(d.job) && <Typography sx={{ fontFamily: MONO, fontSize: 12 }}>{d.job.progress}%</Typography>}
            </Box>
            <LinearProgress variant={workerStarting(d.job) ? "indeterminate" : "determinate"} value={d.job.progress} sx={{ mt: 0.75, height: 4, borderRadius: 2, bgcolor: "rgba(255,255,255,0.12)", "& .MuiLinearProgress-bar": { bgcolor: "#6aa8ff" } }} />
          </Box>
        )}
        {!d.preparing && d.isClean && d.jobSummary && (
          <Box sx={{ width: 160, flex: "none" }} title="Share of the area covered by accepted shapes and painting">
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}><span>Mapped</span><Typography sx={{ fontFamily: MONO, fontSize: 12 }}>{covPct}%</Typography></Box>
            <LinearProgress variant="determinate" value={covPct} sx={{ mt: 0.75, height: 4, borderRadius: 2, bgcolor: "rgba(255,255,255,0.12)", "& .MuiLinearProgress-bar": { bgcolor: TEAL } }} />
            <Typography sx={{ fontSize: 11, color: MUTED, mt: 0.5, whiteSpace: "nowrap" }}>{fmtArea(d.jobSummary.unmapped_m2)} still to map</Typography>
          </Box>
        )}
      </Box>
      {chips.length > 0 && terms.length === 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, px: 0.25 }}>
          <Typography sx={{ fontSize: 12, color: MUTED, mr: 0.25 }}>Try</Typography>
          {chips.map((p) => (
            <Box key={p.key} component="button" onClick={() => add(p.label, p)}
              sx={{ height: 26, px: 1.25, borderRadius: 13, border: `1px solid ${LINE_STRONG}`, bgcolor: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.85)", font: "inherit", fontSize: 12, cursor: "pointer", "&:hover": { borderColor: TEAL, color: TEXT } }}>
              {p.label}
            </Box>
          ))}
        </Box>
      )}
      {terms.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, px: 0.25 }}>
          {terms.map((t) => {
            const on = t.key === activeTerm && mode !== "paint";
            const colour = d.colours[t.target] ?? (t.key === PAINT_TERM ? TEAL : "#b35bd1");
            const frac = t.shown ? t.decided / t.shown : 0;
            const circ = 2 * Math.PI * 7;
            return (
              <Box key={t.key} component="button" onClick={() => { a.pickTerm(t.key); if (mode === "paint") a.toReview(); }}
                title={t.state === "done" ? `${t.decided} of ${t.shown} reviewed` : t.state === "running" ? (d.runningPass && d.job?.stage ? d.job.stage : "Starting") : d.preparing ? "Waiting for the image" : "Waiting its turn"}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.9, height: 30, pl: 1, pr: 1.4, borderRadius: 15, cursor: "pointer", font: "inherit", fontSize: 12.5, whiteSpace: "nowrap",
                  border: `1px solid ${on ? TEAL : LINE_STRONG}`, bgcolor: on ? "rgba(0,212,173,0.12)" : "rgba(255,255,255,0.05)", color: on ? TEXT : "rgba(255,255,255,0.85)", "&:hover": { borderColor: on ? TEAL : "rgba(255,255,255,0.35)" } }}>
                {t.state === "done" ? (
                  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                    <circle cx="9" cy="9" r="7" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2.5" />
                    <circle cx="9" cy="9" r="7" fill="none" stroke={TEAL} strokeWidth="2.5" strokeDasharray={`${circ * frac} ${circ}`} strokeLinecap="round" transform="rotate(-90 9 9)" />
                    <circle cx="9" cy="9" r="3" fill={colour} />
                  </svg>
                ) : t.state === "running" ? <CircularProgress size={14} sx={{ color: "#6aa8ff" }} /> : <Box sx={{ width: 14, height: 14, borderRadius: "50%", border: `2px dashed ${FAINT}` }} />}
                <span>{t.label}</span>
                <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: MUTED }}>
                  {t.state === "done" ? (t.shown !== t.count ? `${t.shown}/${t.count}` : t.count) : t.state === "running" ? runningNote : "queued"}
                </Typography>
              </Box>
            );
          })}
          {unused.length > 0 && (
            <>
              <Box component="button" onClick={(ev: React.MouseEvent<HTMLElement>) => setMoreAnchor(ev.currentTarget)} aria-haspopup="menu" aria-expanded={!!moreAnchor}
                title="Look for something else"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, height: 30, pl: 1, pr: 1.4, borderRadius: 15, cursor: "pointer", font: "inherit", fontSize: 12.5, whiteSpace: "nowrap",
                  border: `1px dashed ${LINE_STRONG}`, bgcolor: "transparent", color: "rgba(255,255,255,0.75)", "&:hover": { borderColor: TEAL, color: TEXT } }}>
                <AddIcon sx={{ fontSize: 15 }} />More
              </Box>
              <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)}
                PaperProps={{ sx: { bgcolor: "#1d2733", color: TEXT, border: `1px solid ${LINE_STRONG}`, mt: 0.5 } }}>
                {unused.map((p) => (
                  <MenuItem key={p.key} sx={menuItemSx} onClick={() => { setMoreAnchor(null); add(p.label, p); }}>{p.label}</MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
